import { FileUrlVariant, type FileUrlDescriptor } from '@magicdoorinc/file';
import { CompanyInfo } from '~/use-cases/types/CompanyInfo';
import { CompanyTheme } from '~/use-cases/types/CompanyTheme';
import { Gateway } from './base/gateway';
import { fileGateway } from './fileGateway';
import { fullStackNetworkManager } from './network/fullStackNetworkManager';
import { MagicRequest, RequestMethod } from './network/magicRequest';

const COMPANY_INFO_URL = '/api/companies/info';

interface CompanyInfoJson {
  id: string;
  name: string;
  email: string;
  subUrl: string;
  language: string;
  phoneNumber: string;
  timeZoneInfo: string;
  customDomain?: string;
  canReceiveRentalApplications?: boolean;
  features?: string[];
  approvedLegalNames?: string[];
  settings?: {
    metadata?: {
      title?: string;
      description?: string;
      themeColor?: string;
      titleTemplate?: string;
      theme?: string;
    };
    contact?: { phone?: string };
    socialLinks?: {
      facebook?: string;
      instagram?: string;
      linkedIn?: string;
      twitter?: string;
      youTube?: string;
      tikTok?: string;
    };
    translationLanguages?: string[];
    logoPublicUrl?: FileUrlDescriptor;
    coverPublicUrl?: FileUrlDescriptor;
    iconPublicUrl?: FileUrlDescriptor;
  };
}

const COMPANY_THEMES = new Set<string>(Object.values(CompanyTheme));

class CompanyGateway extends Gateway {
  public async getCompanyInfo(domain: string): Promise<CompanyInfo> {
    const request: MagicRequest = {
      url: `${COMPANY_INFO_URL}?domain=${encodeURIComponent(domain)}`,
      method: RequestMethod.GET,
      headers: this.createCommonHeaders(),
    };
    const response = await this.sendRequest(request);
    const json: CompanyInfoJson = await response.json();
    return this.toCompanyInfo(json);
  }

  private toCompanyInfo(json: CompanyInfoJson): CompanyInfo {
    const settings = json.settings;
    return {
      id: json.id,
      name: json.name,
      email: json.email,
      url: json.subUrl,
      language: json.language,
      phoneNumber: json.phoneNumber,
      timeZone: json.timeZoneInfo,
      theme: this.toTheme(settings?.metadata?.theme),
      logo: this.toImageUrl(settings?.logoPublicUrl),
      coverUrl: this.toImageUrl(settings?.coverPublicUrl),
      iconUrl: this.toImageUrl(settings?.iconPublicUrl),
      customDomain: json.customDomain,
      facebook: settings?.socialLinks?.facebook,
      instagram: settings?.socialLinks?.instagram,
      linkedIn: settings?.socialLinks?.linkedIn,
      twitter: settings?.socialLinks?.twitter,
      youtube: settings?.socialLinks?.youTube,
      tikTok: settings?.socialLinks?.tikTok,
      metadataTitle: settings?.metadata?.title,
      metadataDescription: settings?.metadata?.description,
      metadataThemeColor: settings?.metadata?.themeColor,
      metadataTitleTemplate: settings?.metadata?.titleTemplate,
      canReceiveRentalApplications: json.canReceiveRentalApplications,
      translationLanguages: settings?.translationLanguages,
      leasingPhoneNumber: settings?.contact?.phone,
      features: json.features,
      approvedLegalNames: json.approvedLegalNames,
    };
  }

  private toTheme(theme?: string): CompanyTheme {
    return theme && COMPANY_THEMES.has(theme) ? (theme as CompanyTheme) : CompanyTheme.Blue;
  }

  private toImageUrl(descriptor?: FileUrlDescriptor): string | undefined {
    return descriptor ? fileGateway.getFileService().buildUrlFromDescriptor(descriptor, FileUrlVariant.Original) : undefined;
  }
}

export const companyGateway = new CompanyGateway(fullStackNetworkManager);
